/**
 * @fileoverview Estilos do "Gerenciar Triagens" (/fluxo), nos dois temas.
 * Botões no padrão da página de fluxos; campos (texto e select) com a cara do
 * select padrão do app (ModernSelect); o resto neutro.
 */

import { makeStyles } from "@material-ui/core/styles";

import { modernFieldStyles } from "../../../../components/ModernSelect";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const textPrimary = isDark ? "#f0f2f5" : "#111318";
  const textSecondary = isDark ? "#a0aec0" : "#4b5563";
  const textFaint = isDark ? "#8a94a6" : "#6b7280";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.12)";
  const inputBorder = isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.2)";
  const surface = isDark ? "#111318" : "#ffffff";
  const surfaceAlt = isDark ? "rgba(255,255,255,0.03)" : "#f8fafc";
  const rowHover = isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.035)";
  const chipBg = isDark ? "rgba(148,163,184,0.14)" : "rgba(71,85,105,0.08)";
  const chipText = isDark ? "#cbd5e1" : "#334155";
  const warn = isDark ? "#fbbf24" : "#b45309";
  const t = "all 160ms cubic-bezier(0.4, 0, 0.2, 1)";

  const btnBase = {
    whiteSpace: "nowrap",
    flexShrink: 0,
    height: 36,
    padding: "0 14px",
    borderRadius: 10,
    fontSize: 13.5,
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    cursor: "pointer",
    border: "none",
    fontFamily: "inherit",
    transition: t,
    "& svg": { width: 15, height: 15, strokeWidth: 1.75, flexShrink: 0 },
    "&:disabled": { opacity: 0.55, cursor: "default" },
  };

  return {
    paper: {
      background: surface,
      borderRadius: 18,
      height: "min(86vh, 860px)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      fontFamily: "'Montserrat', sans-serif",
      "& *": { fontFamily: "'Montserrat', sans-serif" },
      [theme.breakpoints.down("xs")]: { borderRadius: 0, height: "100%" },
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "16px 20px 0",
      flexShrink: 0,
    },
    title: { flex: 1, minWidth: 0, fontSize: 18, fontWeight: 600, color: textPrimary, display: "flex", alignItems: "center", gap: 8 },
    titleSmall: { fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    iconBtn: {
      background: "transparent",
      border: "none",
      padding: 6,
      borderRadius: 8,
      cursor: "pointer",
      color: textSecondary,
      display: "inline-flex",
      "&:hover:not(:disabled)": { background: rowHover, color: textPrimary },
      "&:disabled": { opacity: 0.35, cursor: "default" },
      "& svg": { width: 18, height: 18, strokeWidth: 1.75 },
    },
    // A linha de base é uma sombra interna (não sobra nada para baixo, então não
    // aparece barra de rolagem); a borda da aba ativa passa por cima dela. Em
    // tela estreita as abas rolam de lado, sem barra, como nas abas do MUI.
    tabs: {
      display: "flex",
      gap: 4,
      padding: "10px 20px 0",
      boxShadow: `inset 0 -1px 0 ${border}`,
      flexShrink: 0,
      overflowX: "auto",
      overflowY: "hidden",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
    tab: {
      background: "transparent",
      border: "none",
      borderBottom: "2px solid transparent",
      padding: "8px 10px",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 500,
      color: textFaint,
      whiteSpace: "nowrap",
      "&:hover": { color: textSecondary },
    },
    tabActive: { color: textPrimary, fontWeight: 600, borderBottomColor: textPrimary },
    body: { flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 20px 20px", ...theme.scrollbarStylesSoft },
    footer: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 20px",
      borderTop: `1px solid ${border}`,
      flexShrink: 0,
      flexWrap: "wrap",
    },
    footerInfo: {
      flex: 1,
      minWidth: 180,
      fontSize: 12.5,
      color: textSecondary,
      [theme.breakpoints.down("xs")]: { flexBasis: "100%" },
    },
    footerButtons: { display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" },
    footerWarn: { color: warn, fontWeight: 500 },

    btnPrimary: {
      ...btnBase,
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "&:hover:not(:disabled)": { background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)" },
    },
    btnSecondary: {
      ...btnBase,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
      color: textPrimary,
      border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.16)"}`,
      "&:hover:not(:disabled)": { background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)" },
    },
    btnGhost: {
      ...btnBase,
      height: 32,
      padding: "0 10px",
      background: "transparent",
      color: textSecondary,
      border: `1px dashed ${inputBorder}`,
      "&:hover:not(:disabled)": { color: textPrimary, background: rowHover },
    },

    // Lista
    toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" },
    toggleLabel: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: textSecondary, cursor: "pointer" },
    rows: { display: "flex", flexDirection: "column", gap: 8 },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 14px",
      borderRadius: 12,
      border: `1px solid ${border}`,
      background: surface,
      transition: t,
      "&:hover": { background: rowHover },
      [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
    },
    rowMain: { flex: 1, minWidth: 0, cursor: "pointer" },
    rowTitle: { fontSize: 14, fontWeight: 600, color: textPrimary, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    rowDesc: { fontSize: 12.5, color: textSecondary, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    rowMeta: { fontSize: 12, color: textFaint, marginTop: 4 },
    rowActions: { display: "flex", alignItems: "center", gap: 2, flexShrink: 0 },
    chip: {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 8px",
      borderRadius: 999,
      background: chipBg,
      color: chipText,
      whiteSpace: "nowrap",
    },
    chipOutline: { background: "transparent", border: `1px solid ${inputBorder}` },
    empty: { textAlign: "center", padding: "48px 16px", color: textSecondary, fontSize: 13.5, lineHeight: 1.5 },
    emptyTitle: { fontSize: 15, fontWeight: 600, color: textPrimary, marginBottom: 6 },

    // Formulário
    form: { display: "flex", flexDirection: "column", gap: 16, maxWidth: 760 },
    grid2: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    // Cores, borda e foco do select padrão; aqui só o tamanho das letras.
    field: {
      ...modernFieldStyles(theme),
      "& .MuiInputBase-root": { fontSize: 13.5 },
      "& .MuiInputLabel-root": { fontSize: 13.5 },
      "& .MuiFormHelperText-root": { color: textFaint, marginLeft: 2, fontSize: 11.5 },
    },
    label: { fontSize: 13, fontWeight: 600, color: textPrimary, marginBottom: 4 },
    hint: { fontSize: 12, color: textFaint, lineHeight: 1.45 },
    // Título de seção com a regra de funcionamento num ⓘ (a dica segue a da tela de fluxos).
    titleRow: { display: "flex", alignItems: "center", gap: 4, marginBottom: 6 },
    sectionTitle: { fontSize: 13, fontWeight: 600, color: textPrimary },
    infoBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 22,
      height: 22,
      padding: 0,
      border: "none",
      borderRadius: 6,
      background: "transparent",
      color: textFaint,
      cursor: "help",
      transition: t,
      "& svg": { width: 14, height: 14, strokeWidth: 1.75 },
      "&:hover": { color: textPrimary },
      "&:focus-visible": { outline: `1px solid ${textPrimary}`, outlineOffset: 1 },
    },
    infoTooltip: {
      fontSize: 12,
      lineHeight: 1.45,
      maxWidth: 300,
      color: textSecondary,
      background: surface,
      border: `1px solid ${border}`,
      borderRadius: 8,
      padding: "8px 10px",
      boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.4)" : "0 4px 16px rgba(0,0,0,0.1)",
    },
    // Frase com o campo dentro ("Encerrar a triagem depois de [30] dias").
    sentenceRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13.5, color: textPrimary },
    daysField: { width: 88 },
    warnText: { fontSize: 12.5, color: warn, lineHeight: 1.45 },
    radioGroup: {
      "& .MuiFormControlLabel-label": { fontSize: 13.5, color: textPrimary },
      "& .MuiRadio-root": { color: textSecondary, padding: 6 },
    },
    check: {
      marginRight: 4,
      "& .MuiFormControlLabel-label": { fontSize: 13, color: textPrimary },
      "& .MuiCheckbox-root, & .MuiSwitch-root": { color: textSecondary },
    },

    // Itens
    item: { border: `1px solid ${border}`, borderRadius: 12, background: surface, overflow: "hidden" },
    itemHead: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      cursor: "pointer",
      "&:hover": { background: rowHover },
    },
    itemOrder: {
      width: 24,
      height: 24,
      borderRadius: 12,
      background: chipBg,
      color: chipText,
      fontSize: 12,
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    itemTitle: { flex: 1, minWidth: 0 },
    itemName: { fontSize: 13.5, fontWeight: 600, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    itemNameEmpty: { color: textFaint, fontStyle: "italic", fontWeight: 500 },
    // Resumo da regra das exceções: quebra a linha em vez de cortar (no celular, o dado importa).
    itemNameWrap: { whiteSpace: "normal", overflow: "visible", overflowWrap: "anywhere" },
    itemSub: { fontSize: 12, color: textFaint, marginTop: 1 },
    itemBody: {
      padding: "14px 14px 16px",
      borderTop: `1px solid ${border}`,
      background: surfaceAlt,
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    fieldsBox: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${border}`,
      background: surface,
    },
    fieldRow: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1fr) auto",
      gap: 8,
      alignItems: "center",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr 1fr" },
    },
    fieldFlags: { display: "flex", alignItems: "center", gap: 0, flexWrap: "wrap" },
    // Comparação com a OS: linha inteira embaixo dos controles do campo.
    fieldCompare: { gridColumn: "1 / -1" },

    // Contato ativo
    noteBox: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      padding: "12px 14px",
      borderRadius: 10,
      border: `1px solid ${border}`,
      background: surfaceAlt,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: textSecondary,
    },
    tplList: { display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto", ...theme.scrollbarStylesSoft },
    tplItem: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: 4,
      width: "100%",
      textAlign: "left",
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px solid ${border}`,
      background: surface,
      color: "inherit",
      font: "inherit",
      cursor: "pointer",
      transition: t,
      "&:hover": { background: rowHover },
      "&:disabled": { cursor: "not-allowed", opacity: 0.6, background: surface },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: 1 },
    },
    tplItemActive: { borderColor: textPrimary, boxShadow: `inset 0 0 0 1px ${textPrimary}` },
    tplPreview: {
      whiteSpace: "pre-wrap",
      fontSize: 13,
      lineHeight: 1.5,
      color: textPrimary,
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px solid ${border}`,
      background: surfaceAlt,
    },
    // Ordens de serviço: dias do envio (alternar) e grade de opções.
    dayRow: { display: "flex", flexWrap: "wrap", gap: 6 },
    dayBtn: {
      minWidth: 46,
      height: 32,
      padding: "0 10px",
      borderRadius: 8,
      border: `1px solid ${inputBorder}`,
      background: surface,
      color: textSecondary,
      fontSize: 12.5,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: "pointer",
      transition: t,
      "&:hover": { background: rowHover },
      "&:focus-visible": { outline: `2px solid ${textPrimary}`, outlineOffset: 2 },
    },
    dayBtnOn: {
      background: textPrimary,
      borderColor: textPrimary,
      color: surface,
      "&:hover": { background: textPrimary },
    },
    // Cadastro do contato: os dados que entram no nome (botões no estilo dos dias).
    tokenGroups: { display: "flex", flexDirection: "column", gap: 12 },
    tokenGroupTitle: { fontSize: 12, fontWeight: 600, color: textSecondary, marginBottom: 6 },
    tokenBtn: {
      minWidth: 0,
      maxWidth: "100%",
      fontWeight: 500,
      "& span": { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    },
    previewPlaceholder: { color: textFaint },
    previewInvalid: { color: warn, textDecoration: "underline dotted" },
    optionGrid: { display: "flex", flexWrap: "wrap", columnGap: 16 },
    // Itens que vão para o Contele: separados da escolha da conta.
    exportItems: { marginTop: 14 },
    // Encaminhamento: o "E" entre os dados de uma regra, com uma linha até o botão de tirar.
    andRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: "0.04em",
      color: textFaint,
      "&::after": { content: '""', flex: 1, height: 1, background: border },
      "& > button": { order: 1 },
    },
    timeRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    timeField: { width: 150 },

    varRow: {
      display: "grid",
      gridTemplateColumns: "56px minmax(0, 1fr) minmax(0, 1fr)",
      gap: 8,
      alignItems: "center",
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "48px minmax(0, 1fr)",
        "& > :nth-child(3)": { gridColumn: "1 / -1" },
      },
    },

    // Acompanhamento
    filters: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12, alignItems: "center" },
    filter: { minWidth: 180 },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      fontSize: 13,
      "& th": {
        textAlign: "left",
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        color: textFaint,
        padding: "8px 10px",
        borderBottom: `1px solid ${border}`,
        whiteSpace: "nowrap",
      },
      "& td": {
        padding: "10px 10px",
        borderBottom: `1px solid ${border}`,
        color: textPrimary,
        verticalAlign: "middle",
      },
      "& tbody tr:hover td": { background: rowHover },
    },
    cellMuted: { color: `${textSecondary} !important`, whiteSpace: "nowrap" },
    link: {
      background: "transparent",
      border: "none",
      padding: 0,
      cursor: "pointer",
      color: isDark ? "#8fa6ff" : "#1d4ed8",
      fontSize: 13,
      fontWeight: 500,
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      whiteSpace: "nowrap",
      "& svg": { width: 14, height: 14 },
      "&:hover": { textDecoration: "underline" },
    },
    cards: { display: "flex", flexDirection: "column", gap: 8 },
    loadMore: { display: "flex", justifyContent: "center", marginTop: 14 },
    count: { fontSize: 12.5, color: textSecondary },
  };
});

export default useStyles;
